import { useStore } from '@nanostores/react';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import type { OrdwayPlanBillingPlan } from '@/services/billing/transforms';
import type { PlanInfoModel } from '../types';
import { type BillingResourceLoader, useBillingResourceLoader } from './use-billing-resource-loader';
import { usePlanInfoModel } from './use-plan-info-model';

/**
 * The billing state the available-plans grid and its cards read. Extends the shared
 * {@link PlanInfoModel} (the plan-card inputs) with the grid-only fields. Per-plan derivations
 * (is-subscribed, is-primary, highlights) are computed in `PlanInfo` from this model plus each plan.
 */
export interface AvailablePlansModel extends PlanInfoModel {
  /** Whether to render the startup-program panel (no active subscription and not pending checkout). */
  showStartupPlans: boolean;
  /** The loaded Ordway plans catalog, merged into the static plans by plan type. Undefined for Stripe. */
  ordwayPlans?: OrdwayPlanBillingPlan[];
  accountId: number;
}

/**
 * Reads the billing service + account into the {@link AvailablePlansModel}: the shared
 * {@link usePlanInfoModel} plan-card state plus the grid-only fields (startup panel, Ordway catalog,
 * account id).
 */
export function useAvailablePlansModel(): AvailablePlansModel {
  const { account } = useAuth();
  const model = usePlanInfoModel();
  const ordwayPlans = useStore(billing.availablePlans);

  return {
    ...model,
    showStartupPlans: !model.hasActiveSubscription && !model.isPendingCheckout,
    ordwayPlans,
    accountId: account.id,
  };
}

/**
 * Loads the Ordway available-plans catalog on mount (a no-op for Stripe accounts), tracking a
 * loading/error state so the grid can show a page loader or an error banner with a retry. Also gates
 * on the billing shell's `initialize` (`billing.initialized`, driven by the shell layout) so the grid
 * never renders plan cards against stale subscription/trial state before billing has loaded.
 */
export function useAvailablePlansLoader(): BillingResourceLoader {
  const initialized = useStore(billing.initialized);
  const loader = useBillingResourceLoader(['billing-available-plans'], () => billing.fetchAvailablePlans.perform());
  return {
    isLoading: loader.isLoading || !initialized,
    isError: loader.isError,
    retry: loader.retry,
  };
}
